/**
 * StaffReport.js
 *
 * One person's full report, opened from the Reports table: a day, a week, a
 * month or a year, with their figures against the team's, a breakdown chart,
 * the tasks behind the numbers and what they actually wrote each day.
 *
 * Every figure comes from task_reports.php (action=staff_report), behind the
 * same two-person unlock as the table. This file holds no data of its own.
 *
 * CHART NOTES (applied from the data-viz guidance, validated with its script):
 *  - The bars are blue, NOT the app's teal accent. The accent is #0aa125 -
 *    green - and would be read as the "progress logged" status green beside
 *    it. Blue #2a78d6 / #3987e5 pass every check on #fcfcfd and on the navy
 *    dark surface #0a0e2c.
 *  - Logged / missed are red and green, which deuteranopes cannot separate
 *    (dE 4.1). So they are never colour alone: different shapes (check, cross,
 *    dash), words in the legend and in every tooltip.
 *  - One axis only. Hours are the bars; completions live in the tooltip and
 *    the table view, never on a second scale.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Grid,
  Stack,
  Chip,
  Button,
  IconButton,
  Typography,
  Tooltip,
  TextField,
  MenuItem,
  Alert,
  Skeleton,
  Dialog,
  DialogContent,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  useMediaQuery,
  useTheme,
  alpha,
} from "@mui/material";

import { initials, STAFF_IMAGE_BASE } from "./taskShared";
import { RatingBadge, RatingRow } from "./TaskRatings";

import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TodayOutlinedIcon from "@mui/icons-material/TodayOutlined";
import CheckIcon from "@mui/icons-material/Check";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import RemoveIcon from "@mui/icons-material/Remove";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import TableRowsOutlinedIcon from "@mui/icons-material/TableRowsOutlined";
import StarBorderRoundedIcon from "@mui/icons-material/StarBorderRounded";

/* ================================================================== */
/*  Fixed colours                                                      */
/* ================================================================== */

/** Status colours: fixed, never themed, always with an icon and words. */
const STATUS = { good: "#0ca30c", warning: "#fab219", critical: "#d03b3b" };

/** The single bar series - validated on both of the app's surfaces. */
const BAR = { light: "#2a78d6", dark: "#3987e5" };

const PERIODS = [
  { key: "day", label: "Day" },
  { key: "week", label: "Week" },
  { key: "month", label: "Month" },
  { key: "year", label: "Year" },
];

/* ================================================================== */
/*  Date helpers - all on YYYY-MM-DD strings, in UTC, so a timezone     */
/*  offset can never shift a day.                                      */
/* ================================================================== */

const parse = (iso) => {
  const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};
const iso = (dt) => dt.toISOString().slice(0, 10);
const todayIso = () => {
  const n = new Date();
  return iso(new Date(Date.UTC(n.getFullYear(), n.getMonth(), n.getDate())));
};

/** Move an anchor by one period. Months clamp, so 31 Jan + 1 month = 28 Feb. */
const shift = (anchor, period, dir) => {
  const d = parse(anchor);
  if (period === "day") d.setUTCDate(d.getUTCDate() + dir);
  else if (period === "week") d.setUTCDate(d.getUTCDate() + 7 * dir);
  else if (period === "month") {
    const day = d.getUTCDate();
    d.setUTCDate(1);
    d.setUTCMonth(d.getUTCMonth() + dir);
    const last = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
    d.setUTCDate(Math.min(day, last));
  } else d.setUTCFullYear(d.getUTCFullYear() + dir);
  return iso(d);
};

const fmtDay = (isoDay, opts = { weekday: "short", day: "numeric", month: "short" }) =>
  parse(isoDay).toLocaleDateString("en-GB", { ...opts, timeZone: "UTC" });

const fmtH = (h) => `${(Number(h) || 0).toFixed(1)} h`;

/** A round axis maximum, giving 3-4 clean ticks. */
const niceMax = (v) => {
  if (v <= 0) return 4;
  const steps = [1, 2, 4, 5, 8, 10, 12, 16, 20, 25, 40, 50, 80, 100, 150, 200, 250, 400, 500];
  for (const s of steps) if (v <= s) return s;
  return Math.ceil(v / 100) * 100;
};

/* ================================================================== */
/*  Small pieces                                                       */
/* ================================================================== */

const STATUS_META = {
  logged: { label: "Progress logged", color: STATUS.good, Icon: CheckIcon },
  missed: { label: "Missed - a working day with a task running", color: STATUS.critical, Icon: CloseRoundedIcon },
  not_expected: { label: "Not expected (weekend, holiday or no task)", color: null, Icon: RemoveIcon },
  future: { label: "Still to come", color: null, Icon: null },
};

const StatusMark = ({ ui, status, size = 14 }) => {
  const m = STATUS_META[status];
  if (!m || !m.Icon) return <Box sx={{ width: size, height: size }} />;
  return (
    <m.Icon
      aria-label={m.label}
      sx={{ fontSize: size, color: m.color || alpha(ui.textSecondary, 0.7), display: "block" }}
    />
  );
};

/** One KPI tile: the number in ink, the comparison in words. */
const Kpi = ({ ui, icon, label, value, sub, compare }) => (
  <Box
    sx={{
      bgcolor: ui.surface,
      border: `1px solid ${ui.border}`,
      borderRadius: "14px",
      p: 2,
      height: "100%",
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
      <Box sx={{ color: ui.textSecondary, display: "flex" }}>{icon}</Box>
      <Typography variant="caption" fontWeight={700} color={ui.textSecondary} sx={{ textTransform: "uppercase", letterSpacing: 0.6 }}>
        {label}
      </Typography>
    </Stack>
    <Typography sx={{ fontSize: 26, fontWeight: 800, lineHeight: 1.1, color: ui.textPrimary }}>
      {value}
    </Typography>
    {sub && (
      <Typography variant="caption" color={ui.textSecondary} component="div" sx={{ mt: 0.5 }}>
        {sub}
      </Typography>
    )}
    {compare && (
      <Typography variant="caption" color={ui.textSecondary} component="div" sx={{ mt: 0.25, fontWeight: 600 }}>
        {compare}
      </Typography>
    )}
  </Box>
);

/** "Team avg 62% · 8 points above" - words, never a coloured number. */
const vsTeam = (mine, team, unit = "") => {
  if (team === null || team === undefined) return null;
  if (mine === null || mine === undefined) return `Team average ${team}${unit}`;
  const diff = Math.round((mine - team) * 10) / 10;
  if (Math.abs(diff) < 0.05) return `Team average ${team}${unit} · level with it`;
  return `Team average ${team}${unit} · ${Math.abs(diff)}${unit} ${diff > 0 ? "above" : "below"}`;
};

/* ================================================================== */
/*  The chart                                                          */
/* ================================================================== */

/**
 * Hours per day (or per month), as columns from one baseline.
 *
 * Columns are capped at 24px with a 4px rounded top and a square base; the
 * slot around each one is the hover target, so a short bar is still easy to
 * point at. Only the tallest column carries a value label - the axis, the
 * tooltip and the table view carry the rest.
 */
const HoursChart = ({ ui, isDark, data }) => {
  const year = data.period === "year";
  const buckets = data.buckets || [];
  const max = Math.max(0, ...buckets.map((b) => Number(b.hours) || 0));
  const top = niceMax(max);
  const ticks = [0, top / 2, top];
  const peakKey = max > 0 ? buckets.find((b) => Number(b.hours) === max)?.key : null;
  const bar = isDark ? BAR.dark : BAR.light;
  const H = 190;
  const slotMin = year ? 34 : 18;

  // Selective x labels: every day in a week, every 5th in a month, every month.
  const showLabel = (i, n) =>
    data.period === "week" || year || i === 0 || i === n - 1 || (i + 1) % 5 === 0;

  const tip = (b) => {
    if (year) {
      return (
        <Box sx={{ p: 0.5 }}>
          <Typography variant="caption" fontWeight={800} display="block">
            {parse(b.from).toLocaleDateString("en-GB", { month: "long", year: "numeric", timeZone: "UTC" })}
          </Typography>
          <Typography variant="caption" display="block">{fmtH(b.hours)} logged</Typography>
          <Typography variant="caption" display="block">
            Notes on {b.logged_days} of {b.expected_days} owed days
            {b.logging_rate !== null ? ` (${b.logging_rate}%)` : ""}
          </Typography>
          <Typography variant="caption" display="block">
            {b.completed} completed{b.completed ? `, ${b.on_time} on time` : ""}
          </Typography>
          {b.future && <Typography variant="caption" display="block">Still to come</Typography>}
        </Box>
      );
    }
    const m = STATUS_META[b.status];
    return (
      <Box sx={{ p: 0.5 }}>
        <Typography variant="caption" fontWeight={800} display="block">
          {fmtDay(b.key, { weekday: "long", day: "numeric", month: "long" })}
        </Typography>
        <Typography variant="caption" display="block">{fmtH(b.hours)} logged</Typography>
        <Typography variant="caption" display="block">{m?.label}</Typography>
        {b.completed > 0 && (
          <Typography variant="caption" display="block">
            {b.completed} task{b.completed === 1 ? "" : "s"} completed, {b.on_time} on time
          </Typography>
        )}
        {!b.working && <Typography variant="caption" display="block">Weekend or public holiday</Typography>}
      </Box>
    );
  };

  return (
    <Box>
      <Box sx={{ overflowX: "auto", pb: 1 }}>
        <Box sx={{ display: "flex", minWidth: buckets.length * slotMin + 40 }}>
          {/* y-axis */}
          <Box sx={{ width: 40, flexShrink: 0, position: "relative", height: H }}>
            {ticks.map((t) => (
              <Typography
                key={t}
                variant="caption"
                sx={{
                  position: "absolute",
                  right: 6,
                  bottom: `${(t / top) * 100}%`,
                  transform: "translateY(50%)",
                  color: ui.textSecondary,
                  fontSize: 11,
                }}
              >
                {t}h
              </Typography>
            ))}
          </Box>

          {/* plot */}
          <Box sx={{ flex: 1, position: "relative" }}>
            <Box sx={{ position: "relative", height: H }}>
              {/* hairline gridlines, recessive */}
              {ticks.map((t) => (
                <Box
                  key={t}
                  sx={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: `${(t / top) * 100}%`,
                    borderTop: `1px solid ${alpha(ui.textSecondary, t === 0 ? 0.35 : 0.14)}`,
                  }}
                />
              ))}
              <Box sx={{ position: "absolute", inset: 0, display: "flex" }}>
                {buckets.map((b) => {
                  const h = Number(b.hours) || 0;
                  const shade = !year && !b.working;
                  return (
                    <Tooltip key={b.key} title={tip(b)} arrow placement="top">
                      <Box
                        tabIndex={0}
                        aria-label={`${b.label}: ${fmtH(h)}`}
                        sx={{
                          flex: 1,
                          minWidth: slotMin,
                          height: "100%",
                          display: "flex",
                          alignItems: "flex-end",
                          justifyContent: "center",
                          position: "relative",
                          // the 2px surface gap between neighbouring columns
                          px: "1px",
                          bgcolor: shade ? alpha(ui.textSecondary, 0.05) : "transparent",
                          cursor: "default",
                          outline: "none",
                          "&:hover .bar, &:focus-visible .bar": { filter: "brightness(1.12)" },
                          "&:hover, &:focus-visible": { bgcolor: alpha(ui.textSecondary, shade ? 0.1 : 0.06) },
                        }}
                      >
                        {h > 0 && (
                          <Box
                            className="bar"
                            sx={{
                              width: "100%",
                              maxWidth: 24,
                              height: `${(h / top) * 100}%`,
                              minHeight: 2,
                              bgcolor: bar,
                              borderRadius: "4px 4px 0 0",
                              position: "relative",
                            }}
                          >
                            {b.key === peakKey && (
                              <Typography
                                variant="caption"
                                sx={{
                                  position: "absolute",
                                  bottom: "100%",
                                  left: "50%",
                                  transform: "translate(-50%, -2px)",
                                  fontWeight: 700,
                                  fontSize: 11,
                                  color: ui.textPrimary,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {h.toFixed(1)}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
            </Box>

            {/* status strip - shapes, not just colours */}
            {!year && (
              <Box sx={{ display: "flex", mt: 0.75 }}>
                {buckets.map((b) => (
                  <Box key={b.key} sx={{ flex: 1, minWidth: slotMin, display: "flex", justifyContent: "center" }}>
                    <StatusMark ui={ui} status={b.status} />
                  </Box>
                ))}
              </Box>
            )}

            {/* x labels */}
            <Box sx={{ display: "flex", mt: 0.5 }}>
              {buckets.map((b, i) => (
                <Box key={b.key} sx={{ flex: 1, minWidth: slotMin, textAlign: "center" }}>
                  {showLabel(i, buckets.length) && (
                    <Typography variant="caption" sx={{ fontSize: 11, color: ui.textSecondary, whiteSpace: "nowrap" }}>
                      {b.label}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>

      {!year && (
        <Stack direction="row" flexWrap="wrap" gap={2} sx={{ mt: 1.5 }}>
          {["logged", "missed", "not_expected"].map((s) => (
            <Stack key={s} direction="row" spacing={0.6} alignItems="center">
              <StatusMark ui={ui} status={s} />
              <Typography variant="caption" color={ui.textSecondary}>
                {STATUS_META[s].label}
              </Typography>
            </Stack>
          ))}
          <Stack direction="row" spacing={0.6} alignItems="center">
            <Box sx={{ width: 14, height: 10, bgcolor: alpha(ui.textSecondary, 0.12), borderRadius: "2px" }} />
            <Typography variant="caption" color={ui.textSecondary}>Weekend or public holiday</Typography>
          </Stack>
        </Stack>
      )}
    </Box>
  );
};

/** The same numbers as the chart, as a table - the accessible view. */
const BreakdownTable = ({ ui, data }) => {
  const year = data.period === "year";
  const cell = { borderColor: ui.border, color: ui.textPrimary, py: 0.9 };
  const head = { ...cell, color: ui.textSecondary, fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5 };
  return (
    <TableContainer sx={{ maxHeight: 360 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...head, bgcolor: ui.surfaceAlt }}>{year ? "Month" : "Day"}</TableCell>
            <TableCell sx={{ ...head, bgcolor: ui.surfaceAlt }} align="right">Hours</TableCell>
            <TableCell sx={{ ...head, bgcolor: ui.surfaceAlt }}>{year ? "Progress notes" : "Progress note"}</TableCell>
            <TableCell sx={{ ...head, bgcolor: ui.surfaceAlt }} align="right">Completed</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(data.buckets || []).map((b) => (
            <TableRow key={b.key} hover>
              <TableCell sx={cell}>
                {year
                  ? parse(b.from).toLocaleDateString("en-GB", { month: "long", timeZone: "UTC" })
                  : fmtDay(b.key)}
              </TableCell>
              <TableCell sx={cell} align="right">{fmtH(b.hours)}</TableCell>
              <TableCell sx={cell}>
                {year ? (
                  b.future ? "Still to come" : `${b.logged_days} of ${b.expected_days} owed days${b.logging_rate !== null ? ` (${b.logging_rate}%)` : ""}`
                ) : (
                  <Stack direction="row" spacing={0.8} alignItems="center">
                    <StatusMark ui={ui} status={b.status} />
                    <span>{STATUS_META[b.status]?.label}</span>
                  </Stack>
                )}
              </TableCell>
              <TableCell sx={cell} align="right">
                {b.completed ? `${b.completed} (${b.on_time} on time)` : "—"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

/** A single day, in words: the chart has nothing to compare it with. */
const DaySummary = ({ ui, data }) => {
  const b = (data.buckets || [])[0];
  if (!b) return null;
  const m = STATUS_META[b.status];
  const notes = (data.logs || []).length;
  const line =
    b.status === "logged"
      ? `${fmtH(b.hours)} across ${notes} progress note${notes === 1 ? "" : "s"}.`
      : b.status === "missed"
      ? "A working day with a task running, but no progress note was filed."
      : b.status === "future"
      ? "This day has not happened yet."
      : "No progress note was owed - a weekend, a public holiday, or no task running.";
  return (
    <Stack direction="row" spacing={2} alignItems="center" sx={{ p: 2, borderRadius: "12px", border: `1px solid ${ui.border}`, bgcolor: ui.surfaceAlt }}>
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: "12px",
          display: "grid",
          placeItems: "center",
          bgcolor: m?.color ? alpha(m.color, 0.14) : alpha(ui.textSecondary, 0.12),
          flexShrink: 0,
        }}
      >
        <StatusMark ui={ui} status={b.status} size={24} />
      </Box>
      <Box>
        <Typography fontWeight={800} color={ui.textPrimary}>{m?.label}</Typography>
        <Typography variant="body2" color={ui.textSecondary}>{line}</Typography>
        {b.completed > 0 && (
          <Typography variant="body2" color={ui.textSecondary}>
            {b.completed} task{b.completed === 1 ? "" : "s"} completed this day, {b.on_time} on time.
          </Typography>
        )}
      </Box>
    </Stack>
  );
};

const TaskResult = ({ ui, t }) => {
  if (t.status === "completed") {
    return t.on_time ? (
      <Chip size="small" icon={<CheckIcon sx={{ fontSize: "15px !important", color: `${STATUS.good} !important` }} />} label="On time" variant="outlined" sx={{ borderColor: ui.border, color: ui.textPrimary }} />
    ) : (
      <Chip size="small" icon={<CloseRoundedIcon sx={{ fontSize: "15px !important", color: `${STATUS.critical} !important` }} />} label="Late" variant="outlined" sx={{ borderColor: ui.border, color: ui.textPrimary }} />
    );
  }
  if (t.overdue_now) {
    return <Chip size="small" icon={<ReportProblemOutlinedIcon sx={{ fontSize: "15px !important", color: `${STATUS.critical} !important` }} />} label="Overdue" variant="outlined" sx={{ borderColor: ui.border, color: ui.textPrimary }} />;
  }
  return <Chip size="small" label={t.status === "onhold" ? "On hold" : "In progress"} variant="outlined" sx={{ borderColor: ui.border, color: ui.textSecondary }} />;
};

/* ================================================================== */
/*  Ratings                                                            */
/* ================================================================== */

const RATED_TYPE = { task: "Task", subtask: "Subtask", progress: "Daily report" };

/** How many pieces of rated work show before "Show all". */
const RATED_PREVIEW = 8;

/** What the rated thing was, in words a reader recognises. */
const ratedHeading = (it) => {
  const day = fmtDay(it.work_date, { day: "numeric", month: "short", year: "numeric" });
  if (it.type === "progress") {
    return {
      title: `Daily report, ${fmtDay(it.work_date, { weekday: "short", day: "numeric", month: "short" })}`,
      sub: `${it.task_code} · ${it.task_title}${it.title ? ` › ${it.title}` : ""}`,
    };
  }
  if (it.type === "subtask") {
    return { title: it.title, sub: `${it.task_code} · ${it.task_title} · ${day}` };
  }
  return { title: it.title, sub: `${it.task_code} · ${day}` };
};

/**
 * Every rated piece of their work in the period, with who rated it, in what
 * capacity and why - the part of a rating a number cannot carry.
 */
const RatedWork = ({ ui, k, items, max, firstName }) => {
  const [showAll, setShowAll] = useState(false);
  const shown = showAll ? items : items.slice(0, RATED_PREVIEW);

  const byType = [
    ["Tasks", k.rating_task_avg],
    ["Subtasks", k.rating_subtask_avg],
    ["Daily reports", k.rating_progress_avg],
  ].filter(([, v]) => v !== null && v !== undefined);

  return (
    <Box sx={{ bgcolor: ui.surface, border: `1px solid ${ui.border}`, borderRadius: "14px", p: { xs: 2, md: 2.5 } }}>
      <Typography fontWeight={800} color={ui.textPrimary}>
        How their work was rated
      </Typography>
      <Typography variant="caption" color={ui.textSecondary} component="div" sx={{ mb: 2 }}>
        Did it solve the problem? Ratings from top management, leaders and supervisors, newest work first.
        Dated by when the work was done, not when it was rated.
      </Typography>

      {items.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
          {byType.map(([label, v]) => (
            <Chip
              key={label}
              size="small"
              variant="outlined"
              label={`${label} ${Number(v).toFixed(1)}`}
              sx={{ borderColor: ui.border, color: ui.textPrimary, fontWeight: 700 }}
            />
          ))}
          {k.rated_low > 0 && (
            <Chip
              size="small"
              variant="outlined"
              icon={<ReportProblemOutlinedIcon sx={{ fontSize: "15px !important", color: `${STATUS.critical} !important` }} />}
              label={`${k.rated_low} piece${k.rated_low === 1 ? "" : "s"} rated 2 or less - did not solve the problem`}
              sx={{ borderColor: ui.border, color: ui.textPrimary }}
            />
          )}
        </Stack>
      )}

      <Stack spacing={1.5}>
        {shown.map((it) => {
          const h = ratedHeading(it);
          return (
            <Box
              key={`${it.type}:${it.item_id}`}
              sx={{ border: `1px solid ${ui.border}`, borderRadius: "12px", bgcolor: ui.surfaceAlt, px: 1.75, pt: 1.5, pb: 0.5 }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1.5}>
                <Box minWidth={0}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" rowGap={0.5}>
                    <Box
                      component="span"
                      sx={{ px: 0.8, py: 0.1, borderRadius: "6px", fontSize: 11, fontWeight: 700, color: ui.textSecondary, border: `1px solid ${ui.border}` }}
                    >
                      {RATED_TYPE[it.type] || it.type}
                    </Box>
                    <Typography variant="body2" fontWeight={700} color={ui.textPrimary}>
                      {h.title}
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color={ui.textSecondary} component="div" sx={{ mt: 0.3 }}>
                    {h.sub}
                  </Typography>
                </Box>
                <Box sx={{ flexShrink: 0, pt: 0.2 }}>
                  <RatingBadge ui={ui} average={it.average} count={it.count} />
                </Box>
              </Stack>
              <Divider sx={{ borderColor: ui.border, mt: 1.2 }} />
              {it.ratings.map((r, i) => (
                <Box key={r.rater_id} sx={{ borderTop: i ? `1px solid ${alpha(ui.border, 0.6)}` : 0 }}>
                  <RatingRow ui={ui} r={r} max={max} />
                </Box>
              ))}
            </Box>
          );
        })}
      </Stack>

      {items.length > RATED_PREVIEW && (
        <Button size="small" onClick={() => setShowAll((v) => !v)} sx={{ mt: 1.5, textTransform: "none", fontWeight: 700 }}>
          {showAll ? "Show fewer" : `Show all ${items.length}`}
        </Button>
      )}

      {!items.length && (
        <Typography variant="body2" color={ui.textSecondary} sx={{ textAlign: "center", py: 2 }}>
          None of {firstName ? `${firstName}'s` : "their"} work in this period has been rated yet. That is not
          the same as a low rating.
        </Typography>
      )}
    </Box>
  );
};

/* ================================================================== */
/*  The dialog                                                         */
/* ================================================================== */

const StaffReport = ({ open, staff, defaultDate, ui, isDark, fetchReport, onLocked, onClose }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const [period, setPeriod] = useState("month");
  const [anchor, setAnchor] = useState(defaultDate || todayIso());
  const [view, setView] = useState("chart");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Each fetch gets a number; only the latest may land. Clicking "next"
  // three times quickly must not let a slow earlier answer overwrite it.
  const seq = useRef(0);

  // A different person opens on the same view, from the table's end date.
  useEffect(() => {
    if (open) {
      setAnchor(defaultDate || todayIso());
      setData(null);
    }
  }, [open, staff?.staff_id, defaultDate]);

  useEffect(() => {
    if (!open || !staff) return;
    const mine = ++seq.current;
    setLoading(true);
    setError("");
    fetchReport({ staff_id: staff.staff_id, period, date: anchor }).then((res) => {
      if (mine !== seq.current) return;
      setLoading(false);
      if (!res?.success) {
        if (/Locked|code is needed/i.test(res?.message || "")) {
          onLocked();
          return;
        }
        setError(res?.message || "Could not load this report");
        return;
      }
      setData(res.data);
    });
    // fetchReport and onLocked are stable enough; re-running on their identity
    // would refetch on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, staff?.staff_id, period, anchor]);

  const k = data?.kpis;
  const team = data?.team || {};
  const atLatest = data ? data.to >= data.today : false;

  const logsByDay = useMemo(() => {
    const groups = [];
    let cur = null;
    for (const l of data?.logs || []) {
      if (!cur || cur.day !== l.log_date) {
        cur = { day: l.log_date, hours: 0, items: [] };
        groups.push(cur);
      }
      cur.items.push(l);
      cur.hours += Number(l.hours) || 0;
    }
    return groups;
  }, [data]);

  const thisYear = Number(todayIso().slice(0, 4));
  const years = Array.from({ length: 6 }, (_, i) => thisYear - i);

  const head = { borderColor: ui.border, color: ui.textSecondary, fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5, bgcolor: ui.surfaceAlt };
  const cell = { borderColor: ui.border, color: ui.textPrimary };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { bgcolor: ui.pageBg, borderRadius: fullScreen ? 0 : "18px", overflow: "hidden" } }}
    >
      {/* ---------- Header ---------- */}
      <Box sx={{ background: ui.gradient, color: "#fff", px: { xs: 2, md: 3 }, py: 2.2 }}>
        <Stack direction="row" alignItems="center" spacing={1.6}>
          <Avatar
            src={(staff?.image || data?.staff?.image) ? `${STAFF_IMAGE_BASE}${staff?.image || data?.staff?.image}` : undefined}
            alt={staff?.name}
            sx={{
              width: 46,
              height: 46,
              fontWeight: 800,
              color: "#fff",
              bgcolor: alpha("#fff", 0.2),
              border: `2px solid ${alpha("#fff", 0.6)}`,
              flexShrink: 0,
            }}
          >
            {initials(staff?.name?.split(" ")[0], staff?.name?.split(" ").slice(-1)[0])}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h6" fontWeight={800} noWrap>
              {staff?.name}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }} noWrap>
              {[staff?.role, staff?.department].filter(Boolean).join(" · ")}
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: "#fff" }} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      {/* ---------- Period controls ---------- */}
      <Box sx={{ px: { xs: 2, md: 3 }, py: 1.5, bgcolor: ui.surface, borderBottom: `1px solid ${ui.border}` }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ xs: "stretch", md: "center" }} justifyContent="space-between">
          <ToggleButtonGroup
            exclusive
            size="small"
            value={period}
            onChange={(_, v) => v && setPeriod(v)}
            sx={{
              "& .MuiToggleButton-root": { textTransform: "none", fontWeight: 700, px: 2 },
              "& .Mui-selected": { bgcolor: `${alpha(ui.accent, 0.14)} !important`, color: `${ui.textPrimary} !important` },
            }}
          >
            {PERIODS.map((p) => (
              <ToggleButton key={p.key} value={p.key}>
                {p.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <Stack direction="row" spacing={1} alignItems="center" justifyContent={{ xs: "space-between", md: "flex-end" }}>
            <IconButton size="small" onClick={() => setAnchor(shift(anchor, period, -1))} aria-label="Previous period">
              <ChevronLeftIcon />
            </IconButton>

            {period === "year" ? (
              <TextField
                select
                size="small"
                value={anchor.slice(0, 4)}
                onChange={(e) => setAnchor(`${e.target.value}-01-01`)}
                sx={{ minWidth: 110 }}
              >
                {years.map((y) => (
                  <MenuItem key={y} value={String(y)}>
                    {y}
                  </MenuItem>
                ))}
              </TextField>
            ) : period === "month" ? (
              <TextField
                type="month"
                size="small"
                value={anchor.slice(0, 7)}
                onChange={(e) => e.target.value && setAnchor(`${e.target.value}-01`)}
                inputProps={{ max: todayIso().slice(0, 7) }}
              />
            ) : (
              <TextField
                type="date"
                size="small"
                value={anchor}
                onChange={(e) => e.target.value && setAnchor(e.target.value)}
                inputProps={{ max: todayIso() }}
                helperText={null}
              />
            )}

            <IconButton
              size="small"
              onClick={() => setAnchor(shift(anchor, period, 1))}
              disabled={atLatest}
              aria-label="Next period"
            >
              <ChevronRightIcon />
            </IconButton>
            <Button
              size="small"
              startIcon={<TodayOutlinedIcon />}
              onClick={() => setAnchor(todayIso())}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Today
            </Button>
          </Stack>
        </Stack>
        {data && (
          <Typography variant="subtitle2" fontWeight={800} color={ui.textPrimary} sx={{ mt: 1.2 }}>
            {data.label}
          </Typography>
        )}
      </Box>

      {loading && <LinearProgress sx={{ height: 2 }} />}

      <DialogContent sx={{ px: { xs: 2, md: 3 }, py: 2.5 }}>
        {error && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: "12px" }}>
            {error}
          </Alert>
        )}

        {!data && !error && (
          <Grid container spacing={2} columns={{ xs: 12, lg: 14 }}>
            {Array.from({ length: 7 }).map((_, i) => (
              <Grid key={i} size={{ xs: 6, md: 3, lg: 2 }}>
                <Skeleton variant="rounded" height={110} sx={{ borderRadius: "14px" }} />
              </Grid>
            ))}
          </Grid>
        )}

        {data && k && (
          <Stack spacing={3} sx={{ opacity: loading ? 0.6 : 1, transition: "opacity .15s" }}>
            {/* ---------- KPIs ---------- */}
            {/* Fourteen columns on a wide screen, so seven tiles make one row. */}
            <Grid container spacing={2} columns={{ xs: 12, lg: 14 }}>
              <Grid size={{ xs: 6, md: 3, lg: 2 }}>
                <Kpi
                  ui={ui}
                  icon={<InsightsOutlinedIcon fontSize="small" />}
                  label="Index"
                  value={k.index ?? "—"}
                  sub={k.index === null ? "Nothing to measure in this period" : "Completion · on time · notes"}
                  compare={vsTeam(k.index, team.index)}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 3, lg: 2 }}>
                <Kpi
                  ui={ui}
                  icon={<TaskAltOutlinedIcon fontSize="small" />}
                  label="Completed"
                  value={`${k.tasks_completed}`}
                  sub={`${k.completed_on_time} on time · ${k.completed_late} late`}
                  compare={`${k.tasks_assigned} task${k.tasks_assigned === 1 ? "" : "s"} running in this period`}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 3, lg: 2 }}>
                <Kpi
                  ui={ui}
                  icon={<ScheduleOutlinedIcon fontSize="small" />}
                  label="On time"
                  value={k.on_time_rate === null ? "—" : `${k.on_time_rate}%`}
                  sub={
                    k.avg_days_vs_deadline === null
                      ? "No completions to judge"
                      : k.avg_days_vs_deadline <= 0
                      ? `${Math.abs(k.avg_days_vs_deadline)} d early on average`
                      : `${k.avg_days_vs_deadline} d late on average`
                  }
                  compare={vsTeam(k.on_time_rate, team.on_time_rate, "%")}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 3, lg: 2 }}>
                <Kpi
                  ui={ui}
                  icon={<EditNoteOutlinedIcon fontSize="small" />}
                  label="Progress notes"
                  value={k.logging_rate === null ? "—" : `${k.logging_rate}%`}
                  sub={`${k.days_logged} of ${k.days_expected} owed working days`}
                  compare={vsTeam(k.logging_rate, team.logging_rate, "%")}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 3, lg: 2 }}>
                <Kpi
                  ui={ui}
                  icon={<AccessTimeOutlinedIcon fontSize="small" />}
                  label="Hours"
                  value={Number(k.hours_logged).toFixed(1)}
                  sub="Recorded in progress notes"
                  compare={vsTeam(k.hours_logged, team.hours_logged, " h")}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 3, lg: 2 }}>
                <Kpi
                  ui={ui}
                  icon={<StarBorderRoundedIcon fontSize="small" />}
                  label="Rating"
                  value={k.rating_avg === null ? "—" : `${Number(k.rating_avg).toFixed(1)} / ${data.rating_scale?.max || 6}`}
                  sub={
                    k.ratings_count
                      ? `${k.ratings_count} rating${k.ratings_count === 1 ? "" : "s"} on ${k.rated_items} piece${k.rated_items === 1 ? "" : "s"} of work`
                      : "Nothing of theirs rated in this period"
                  }
                  compare={vsTeam(k.rating_avg, team.rating_avg, " stars")}
                />
              </Grid>
              <Grid size={{ xs: 6, md: 3, lg: 2 }}>
                <Kpi
                  ui={ui}
                  icon={<ReportProblemOutlinedIcon fontSize="small" />}
                  label="Right now"
                  value={`${k.open_now} open`}
                  sub={
                    k.overdue_now > 0
                      ? `${k.overdue_now} overdue today`
                      : "Nothing overdue today"
                  }
                  compare="Current, not limited to the period"
                />
              </Grid>
            </Grid>

            {/* ---------- Breakdown ---------- */}
            <Box sx={{ bgcolor: ui.surface, border: `1px solid ${ui.border}`, borderRadius: "14px", p: { xs: 2, md: 2.5 } }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} gap={1}>
                <Box>
                  <Typography fontWeight={800} color={ui.textPrimary}>
                    {data.period === "day"
                      ? "The day"
                      : data.period === "year"
                      ? "Hours logged per month"
                      : "Hours logged per day"}
                  </Typography>
                  {data.period !== "day" && (
                    <Typography variant="caption" color={ui.textSecondary}>
                      Point at a column for the detail
                    </Typography>
                  )}
                </Box>
                {data.period !== "day" && (
                  <ToggleButtonGroup exclusive size="small" value={view} onChange={(_, v) => v && setView(v)}>
                    <ToggleButton value="chart" aria-label="Chart view">
                      <BarChartOutlinedIcon fontSize="small" />
                    </ToggleButton>
                    <ToggleButton value="table" aria-label="Table view">
                      <TableRowsOutlinedIcon fontSize="small" />
                    </ToggleButton>
                  </ToggleButtonGroup>
                )}
              </Stack>

              {data.period === "day" ? (
                <DaySummary ui={ui} data={data} />
              ) : view === "chart" ? (
                <HoursChart ui={ui} isDark={isDark} data={data} />
              ) : (
                <BreakdownTable ui={ui} data={data} />
              )}
            </Box>

            {/* ---------- Tasks ---------- */}
            <Box sx={{ bgcolor: ui.surface, border: `1px solid ${ui.border}`, borderRadius: "14px", overflow: "hidden" }}>
              <Box sx={{ px: { xs: 2, md: 2.5 }, pt: 2, pb: 1.5 }}>
                <Typography fontWeight={800} color={ui.textPrimary}>
                  Tasks in this period
                </Typography>
                <Typography variant="caption" color={ui.textSecondary}>
                  Running at some point in the period, or finished within it
                </Typography>
              </Box>
              <TableContainer>
                <Table size="small" sx={{ minWidth: 800 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={head}>Task</TableCell>
                      <TableCell sx={head}>Role</TableCell>
                      <TableCell sx={head}>Dates</TableCell>
                      <TableCell sx={head} align="right">Progress</TableCell>
                      <TableCell sx={head}>Result</TableCell>
                      <TableCell sx={head} align="right">Rating</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(data.tasks || []).map((t) => (
                      <TableRow key={t.id} hover>
                        <TableCell sx={cell}>
                          <Typography variant="body2" fontWeight={700} color={ui.textPrimary}>
                            {t.title}
                          </Typography>
                          <Typography variant="caption" color={ui.textSecondary}>
                            {t.task_code} · {t.priority} priority
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ ...cell, textTransform: "capitalize" }}>{t.role || "—"}</TableCell>
                        <TableCell sx={{ ...cell, whiteSpace: "nowrap" }}>
                          {fmtDay(t.start_date, { day: "numeric", month: "short" })} – {fmtDay(t.deadline, { day: "numeric", month: "short", year: "numeric" })}
                          {t.completed_at && (
                            <Typography variant="caption" color={ui.textSecondary} display="block">
                              Done {fmtDay(String(t.completed_at).slice(0, 10), { day: "numeric", month: "short" })}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={cell} align="right">
                          {t.progress}%
                        </TableCell>
                        <TableCell sx={cell}>
                          <TaskResult ui={ui} t={t} />
                        </TableCell>
                        <TableCell sx={cell} align="right">
                          {String(t.role).toLowerCase() === "supervisor" && !t.rating ? (
                            <Tooltip title="Supervisors oversee the work, so a task's rating is credited to its leader and collaborators">
                              <Typography variant="caption" color={ui.textSecondary}>
                                Supervised
                              </Typography>
                            </Tooltip>
                          ) : (
                            <RatingBadge ui={ui} average={t.rating?.average} count={t.rating?.count} />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {!(data.tasks || []).length && (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ ...cell, color: ui.textSecondary, textAlign: "center", py: 3 }}>
                          No tasks in this period.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* ---------- Ratings ---------- */}
            <RatedWork
              key={`${data.from}:${data.to}:${data.staff?.id}`}
              ui={ui}
              k={k}
              items={data.rated_work || []}
              max={data.rating_scale?.max || 6}
              firstName={staff?.name ? staff.name.split(" ")[0] : ""}
            />

            {/* ---------- Work log ---------- */}
            <Box sx={{ bgcolor: ui.surface, border: `1px solid ${ui.border}`, borderRadius: "14px", p: { xs: 2, md: 2.5 } }}>
              <Typography fontWeight={800} color={ui.textPrimary}>
                What they recorded
              </Typography>
              <Typography variant="caption" color={ui.textSecondary} component="div" sx={{ mb: 2 }}>
                Their daily progress notes, newest first
              </Typography>

              {data.logs_truncated && (
                <Alert severity="info" sx={{ mb: 2, borderRadius: "12px" }}>
                  Showing the latest 1,000 notes. Choose a shorter period to see the rest.
                </Alert>
              )}

              {logsByDay.map((g) => (
                <Box key={g.day} sx={{ mb: 2.5, "&:last-of-type": { mb: 0 } }}>
                  <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight={800} color={ui.textPrimary}>
                      {fmtDay(g.day, { weekday: "long", day: "numeric", month: "long" })}
                    </Typography>
                    <Typography variant="caption" color={ui.textSecondary}>
                      {fmtH(g.hours)}
                    </Typography>
                  </Stack>
                  <Stack spacing={1}>
                    {g.items.map((l) => (
                      <Box
                        key={l.id}
                        sx={{
                          p: 1.5,
                          borderRadius: "10px",
                          border: `1px solid ${ui.border}`,
                          borderLeft: `3px solid ${alpha(isDark ? BAR.dark : BAR.light, 0.7)}`,
                          bgcolor: ui.surfaceAlt,
                        }}
                      >
                        <Stack direction="row" justifyContent="space-between" gap={1} flexWrap="wrap">
                          <Typography variant="body2" fontWeight={700} color={ui.textPrimary}>
                            {l.task_code} · {l.task_title}
                            {l.subtask_title ? ` › ${l.subtask_title}` : ""}
                          </Typography>
                          <Stack direction="row" spacing={0.75}>
                            <Chip size="small" label={fmtH(l.hours)} sx={{ height: 22 }} />
                            {l.progress !== null && <Chip size="small" label={`${l.progress}%`} sx={{ height: 22 }} />}
                            {l.work_mode && <Chip size="small" variant="outlined" label={l.work_mode} sx={{ height: 22, textTransform: "capitalize" }} />}
                            {l.rating && (
                              <Tooltip title="Average rating for this report - see How their work was rated">
                                <Box sx={{ display: "flex", alignItems: "center", px: 0.9, height: 22, borderRadius: "11px", border: `1px solid ${ui.border}`, bgcolor: ui.surface }}>
                                  <RatingBadge ui={ui} average={l.rating.average} count={l.rating.count} variant="caption" />
                                </Box>
                              </Tooltip>
                            )}
                          </Stack>
                        </Stack>
                        <Typography variant="body2" color={ui.textPrimary} sx={{ mt: 0.75, whiteSpace: "pre-wrap" }}>
                          {l.work_done}
                        </Typography>
                        {l.blockers && (
                          <Stack direction="row" spacing={0.75} alignItems="flex-start" sx={{ mt: 0.75 }}>
                            <WarningAmberOutlinedIcon sx={{ fontSize: 17, color: STATUS.warning, mt: "1px" }} />
                            <Typography variant="body2" color={ui.textSecondary}>
                              <strong>Blocked:</strong> {l.blockers}
                            </Typography>
                          </Stack>
                        )}
                      </Box>
                    ))}
                  </Stack>
                </Box>
              ))}

              {!logsByDay.length && (
                <Typography variant="body2" color={ui.textSecondary} sx={{ textAlign: "center", py: 2 }}>
                  No progress notes in this period.
                </Typography>
              )}
            </Box>

            <Divider sx={{ borderColor: ui.border }} />
            <Typography variant="caption" color={ui.textSecondary}>
              These figures describe what was recorded in the task system, not everything
              {staff?.name ? ` ${staff.name.split(" ")[0]}` : ""} did. Use them as one input
              to a conversation.
            </Typography>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default StaffReport;
